import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withStyles } from "@material-ui/core";
import { withTranslation } from "react-i18next";
import { images } from "../../utils/images";
import Selector2 from "../Selector2";
import { CloudUpload, Check, Close, Error } from "@material-ui/icons";
import {
  pushNotification,
  uploadFile,
  fileUploaded,
} from "../../actions/index";
import { getBase64 } from "../../utils/tools";
import moment from "moment";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { format } from "numeral";

const noExpiryDateCountries = [
  "PH",
  "IN",
  "ID",
  "KE",
  "BR",
  "ZA",
  "RU",
  "PL",
  "AZ",
  "CO",
  "NG",
  "TR",
  "GR",
  "MY",
  "NP",
];

// Create File Input Selector
const createFileSelector = (side, listener) => {
  const fileSelector = document.createElement("input");
  fileSelector.setAttribute("type", "file");
  fileSelector.setAttribute("accept", ".png, .jpg, .pdf");
  fileSelector.addEventListener(
    "change",
    function (e) {
      listener(e.target.files[0], side);
    },
    false
  );
  return fileSelector;
};

function DocumentUpload(props) {
  const {
    t,
    classes,
    documentType,
    pushNotification,
    account,
    wasFileUploaded,
  } = props;
  const idTypes = [
    t("passport"),
    t("driversLicense"),
    t("prcard"),
    t("citizenshipcard"),
    t("healthcardphoto"),
    t("nationalid"),
    t("socialserviceid"),
  ];

  const billTypes = [
    t("utility"),
    t("taxDoc"),
    t("creditCard"),
    t("bankStatement"),
  ];

  const [missingFields, setMissingFields] = useState({
    fields: [],
    missing: false,
  });
  const [idType, setIdType] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [billType, setBillType] = useState("");
  const [billDate, setBillDate] = useState(null);
  const [docNumber, setDocNumber] = useState("");
  const [dateType, setDateType] = useState(false);
  const [idFront, setIdFront] = useState({
    photoUrl: "",
    file: null,
  });
  const [idBack, setIdBack] = useState({
    photoUrl: "",
    file: null,
  });
  const [selfie, setSelfie] = useState({
    photoUrl: "",
    file: null,
  });
  const [poaFile, setPoa] = useState({
    photoUrl: "",
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  useEffect(
    function () {
      cleanState();
    },
    [documentType]
  );

  useEffect(() => {
    if (wasFileUploaded) {
      setUploading(false);
      cleanState();
      props.fileUploaded(false);
    }
  }, [wasFileUploaded]);

  const cleanState = () => {
    setExpiryDate(null);
    setDocNumber("");
    setIdType("");
    setBillType("");
    setBillDate(null);
    setMissingFields({
      fields: [],
      missing: false,
    });
    setSelfie({ photoUrl: "", file: null });
    setPoa({ photoUrl: "", file: null });
    setIdFront({
      photoUrl: "",
      file: null,
    });
    setIdBack({
      photoUrl: "",
      file: null,
    });
  };

  // Handle Images Uplaoded
  const handleImageUpload = (file, side) => {
    let acceptedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
    ];
    if (!acceptedTypes.includes(file.type)) {
      props.pushNotification({ message: "_invalidFileType", type: "error" });
      return;
    }

    if (file.size > 10485760) {
      props.pushNotification({ message: "_fileTooBig", type: "error" });
      return;
    }

    const photo = URL.createObjectURL(file);
    switch (side) {
      case "front":
        setIdFront({ photoUrl: photo, file: file });
        break;
      case "back":
        setIdBack({ photoUrl: photo, file: file });
        break;
      case "selfie":
        setSelfie({ photoUrl: photo, file: file });
        break;
      case "poa":
        setPoa({ photoUrl: photo, file: file });
        break;
    }
  };

  // Create File inputs
  const frontIDInput = createFileSelector("front", handleImageUpload);
  const backIDInput = createFileSelector("back", handleImageUpload);
  const selfieInput = createFileSelector("selfie", handleImageUpload);
  const poaInput = createFileSelector("poa", handleImageUpload);

  // Browse Files
  const browseFiles = (side) => {
    switch (side) {
      case "front":
        frontIDInput.click();
        break;
      case "back":
        backIDInput.click();
        break;
      case "selfie":
        selfieInput.click();
        break;
      case "poa":
        poaInput.click();
        break;
    }
  };

  // Handle file drop
  const handleDrop = (ev, side) => {
    ev.preventDefault();

    if (ev.dataTransfer.items) {
      //   // Use DataTransferItemList interface to access the file(s)
      for (var i = 0; i < ev.dataTransfer.items.length; i++) {
        // If dropped items aren't files, reject them
        if (ev.dataTransfer.items[i].kind === "file") {
          var file = ev.dataTransfer.items[i].getAsFile();
          handleImageUpload(file, side);
        }
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      for (var i = 0; i < ev.dataTransfer.files.length; i++) {
        handleImageUpload(ev.dataTransfer.files[i], side);
      }
    }
  };

  // Confirm and Upload files
  const confirmAndUpload = async () => {
    if (documentType === "id") {
      let isFront = idFront.file ? true : false;
      let isType = idType !== "" ? true : false;
      let isBack =
        idType.toLowerCase() === "passport" ? true : idBack.file ? true : false;
      let isDocNum = docNumber !== "" ? true : false;
      let isExpiryDate =
        expiryDate === "" || expiryDate === "Invalid date" ? false : true;

      if (noExpiryDateCountries.includes(account.account.profile.country)) {
        isExpiryDate = true;
      }

      if (!isFront || !isType || !isBack || !isDocNum || !isExpiryDate) {
        let missingFieldsArr = [];
        if (!isFront) missingFieldsArr.push("idFront");
        if (!isBack) missingFieldsArr.push("idBack");
        if (!isType) missingFieldsArr.push("idType");
        if (!isDocNum) missingFieldsArr.push("idDocNum");
        if (!isExpiryDate) missingFieldsArr.push("idDate");

        setMissingFields({ fields: missingFieldsArr, missing: true });
        return pushNotification({ message: "missingIDFields", type: "error" });
      }

      let front64 = await getBase64(idFront.file);
      let back64 =
        idType.toLowerCase() === "passport" ? "" : await getBase64(idBack.file);
      setUploading(true);
      props.uploadFile({
        type: idType,
        number: docNumber,
        date: null,
        expiry: moment(expiryDate).format("YYYY-MM-DD"),
        files: [front64, back64],
      });
    }

    if (documentType === "selfie") {
      let isSelfie = selfie.file ? true : false;

      if (!isSelfie) {
        setMissingFields({ fields: ["selfie"], missing: true });
        return pushNotification({
          message: "File has not been uploaded",
          type: "error",
        });
      }

      let selfie64 = await getBase64(selfie.file);
      setUploading(true);
      props.uploadFile({
        type: "selfie",
        number: null,
        date: null,
        expiry: null,
        files: [selfie64],
      });
    }

    if (documentType === "poa") {
      let isBillType = billType !== "" ? true : false;
      let isBillDate =
        billDate === "" || billDate === "Invalid date" ? false : true;
      let isBill = poaFile.file !== null ? true : false;

      if (!isBill || !isBillType || !isBillDate) {
        let missingFieldsArr = [];
        if (!isBill) missingFieldsArr.push("bill");
        if (!isBillDate) missingFieldsArr.push("billDate");
        if (!isBillType) missingFieldsArr.push("billType");

        setMissingFields({ fields: missingFieldsArr, missing: true });
        return pushNotification({ message: "missingIDFields", type: "error" });
      }
      let poa64 = await getBase64(poaFile.file);
      setUploading(true);

      props.uploadFile({
        type: billType,
        number: null,
        date: moment(billDate).format("YYYY-MM-DD"),
        expiry: null,
        files: [poa64],
      });
    }
  };

  // Validate dates
  //// ID date
  // useEffect(() => {
  //   let now = moment();
  //   let expiry = moment(expiryDate);

  //   let isBefore = expiry.isBefore(now);

  //   if (isBefore) {
  //     props.pushNotification({ message: "_invalidDateBefore", type: "error" });
  //     setExpiryDate(null);
  //   } else {
  //     let diff = expiry.diff(now, "hours");
  //     if (diff <= 24) {
  //       props.pushNotification({
  //         message: "_invalidDateTomorrow",
  //         type: "error",
  //       });
  //       setExpiryDate(null);
  //     }
  //   }
  // }, [expiryDate]);

  //// POA date
  // useEffect(() => {
  //   let now = moment();
  //   let dateOnBill = moment(billDate);

  //   let isAfter = dateOnBill.isAfter(now);

  //   if (isAfter) {
  //     props.pushNotification({
  //       message: "_invalidDateFuture",
  //       type: "error",
  //     });
  //     setBillDate(null);
  //   } else {
  //     let diff = now.diff(dateOnBill, "days");
  //     if (diff > 90) {
  //       props.pushNotification({
  //         message: "_invalidDateTooOld",
  //         type: "error",
  //       });
  //       setBillDate(null);
  //     }
  //   }
  // }, [billDate]);

  const handleDateChange = (date) => {
    if (date === null) {
      setBillDate(null);
    } else {
      let formattedDate = date.format("YYYY-MM-DD");

      console.log("DATE", formattedDate);

      if (formattedDate !== "Invalid date") {
        let now = moment();
        let dateOnBill = moment(formattedDate);

        let isAfter = dateOnBill.isAfter(now);
        let diff = now.diff(dateOnBill, "days");

        if (isAfter) {
          props.pushNotification({
            message: "_invalidDateFuture",
            type: "error",
          });
          setBillDate(null);
        } else if (diff > 90) {
          props.pushNotification({
            message: "_invalidDateTooOld",
            type: "error",
          });
          setBillDate(null);
        } else {
          setBillDate(formattedDate);
        }
      } else {
        setBillDate(formattedDate);
      }
    }
  };

  const handleExpiryDate = (date) => {
    if (date === null) {
      setExpiryDate(null);
    } else {
      let formattedDate = date.format("YYYY-MM-DD");

      if (formattedDate !== "Invalid date") {
        let now = moment();
        let expiry = moment(formattedDate);

        let isBefore = expiry.isBefore(now);
        let diff = expiry.diff(now, "hours");

        if (isBefore) {
          props.pushNotification({
            message: "_invalidDateBefore",
            type: "error",
          });
          setExpiryDate(null);
        } else if (diff <= 24) {
          props.pushNotification({
            message: "_invalidDateTomorrow",
            type: "error",
          });
          setExpiryDate(null);
        } else {
          setExpiryDate(formattedDate);
        }
      } else {
        setExpiryDate(formattedDate);
      }
    }
  };

  if (documentType === "id") {
    return (
      <div className={classes.documentUploadContainer}>
        <h2>{t("idTitle")}</h2>
        <ul>
          <li>{t("idLi1")}</li>
          <li>{t("idLi2")}</li>
          <li>{t("idLi3")}</li>
          <li>{t("idLi4")}</li>
        </ul>
        <h2>{t("example")}</h2>
        <div className="exampleContainer">
          <div className="example">
            <label>{t("idExampleTitle")}</label>
            <img
              style={{ paddingBottom: 4 }}
              src={images.illustrations.idFront}
            />
            <img src={images.illustrations.idBack} />
          </div>
          <div className="example">
            <label>{t("passport")}</label>
            <img src={images.illustrations.passport} />
          </div>
        </div>
        <div className="uploadContainer">
          <div className="inputs">
            <div
              className={`singleInput ${
                missingFields.missing
                  ? missingFields.fields.includes("idType")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
            >
              <h2>{t("_documentDetails")}</h2>
              <Selector2
                options={idTypes}
                selectorHandle={setIdType}
                selectedOption={idType}
                placeholder={t("_docType")}
              />
            </div>
            <div
              className={`singleInput ${
                missingFields.missing
                  ? missingFields.fields.includes("idDate")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
            >
              {/* <label>
                {noExpiryDateCountries.includes(
                  account.account.profile?.country
                )
                  ? t("_documentDateIf")
                  : t("documentDate")}
              </label> */}
              {/* <input
                type={dateType ? "date" : "text"}
                onFocus={() => setDateType(true)}
                onBlur={(e) => handleExpiryDate(e)}
                className="textInput"
                placeholder={
                  noExpiryDateCountries.includes(
                    account.account.profile?.country
                  )
                    ? t("_documentDateIf")
                    : t("documentDate")
                }
              /> */}

              <KeyboardDatePicker
                value={expiryDate}
                onChange={(e) => handleExpiryDate(e)}
                views={["year", "month", "date"]}
                format="YYYY/MM/DD"
                classes={{ root: classes.picker }}
                placeholder={
                  noExpiryDateCountries.includes(
                    account.account.profile?.country
                  )
                    ? t("_documentDateIf")
                    : t("documentDate")
                }
              />
            </div>
            <div
              className={`singleInput ${
                missingFields.missing
                  ? missingFields.fields.includes("idDocNum")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
            >
              <input
                type="text"
                value={docNumber}
                onChange={(e) => setDocNumber(e.target.value)}
                className="textInput"
                placeholder={t("documentNumber")}
              />
            </div>
          </div>

          <div className="idFieldInput">
            <h2>{t("frontOfID")}</h2>
            <div
              className={`dragArea ${
                missingFields.missing
                  ? missingFields.fields.includes("idFront")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
              onDrop={(e) => handleDrop(e, "front")}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => browseFiles("front")}
            >
              {idFront.file !== null ? <Check /> : <CloudUpload />}
              {idFront.file !== null ? (
                <p>{t("_successUpload")}</p>
              ) : (
                <p>{t("dragAndDrop")}</p>
              )}
              {idFront.file !== null ? (
                <p
                  className="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIdFront({ photoUrl: "", file: null });
                  }}
                >
                  {t("delete")} <Close />
                </p>
              ) : (
                ""
              )}
            </div>
          </div>

          {idType.toLowerCase() === "passport" ? (
            " "
          ) : (
            <div className="idFieldInput">
              <h2>{t("backOfID")}</h2>
              <div
                className={`dragArea ${
                  missingFields.missing
                    ? missingFields.fields.includes("idBack")
                      ? classes.missingField
                      : ""
                    : ""
                }`}
                onDrop={(e) => handleDrop(e, "back")}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => browseFiles("back")}
              >
                {idBack.file !== null ? <Check /> : <CloudUpload />}
                {idBack.file !== null ? (
                  <p>{t("_successUpload")}</p>
                ) : (
                  <p>{t("dragAndDrop")}</p>
                )}
                {idBack.file !== null ? (
                  <p
                    className="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdBack({ photoUrl: "", file: null });
                    }}
                  >
                    {t("delete")} <Close />
                  </p>
                ) : (
                  ""
                )}
              </div>
            </div>
          )}
          <div className="btnContainer">
            <button
              className="confirmButton"
              disabled={uploading}
              onClick={(e) => confirmAndUpload(e)}
            >
              {uploading ? t("_uploading") : t("confirm")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (documentType === "selfie") {
    return (
      <div className={classes.documentUploadContainer}>
        <h2>{t("selfieTitle")}</h2>
        <ul>
          <li>{t("selfieLi1")}</li>
          <li>{t("selfieLi2")}</li>
          <li>{t("selfieLi3")}</li>
          <li>{t("selfieLi4")}</li>
        </ul>
        <h2>{t("example")}</h2>
        <div className="exampleContainer">
          <img
            style={{
              paddingBottom: 4,
              width: "45%",
              margin: "0 auto",
              height: "100%",
            }}
            src={images.illustrations.selfieSample}
          />
        </div>
        <div className="uploadContainer">
          <div className="idFieldInput">
            <h2>{t("selfieInfo")}</h2>
            <div
              className={`dragArea ${
                missingFields.missing
                  ? missingFields.fields.includes("selfie")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
              onDrop={(e) => handleDrop(e, "selfie")}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => browseFiles("selfie")}
            >
              {selfie.file !== null ? <Check /> : <CloudUpload />}
              {selfie.file !== null ? (
                <p>{t("_successUpload")}</p>
              ) : (
                <p>{t("dragAndDrop")}</p>
              )}
              {selfie.file !== null ? (
                <p
                  className="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelfie({ photoUrl: "", file: null });
                  }}
                >
                  {t("delete")} <Close />
                </p>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="btnContainer">
            <button
              className="confirmButton"
              disabled={uploading}
              onClick={(e) => confirmAndUpload(e)}
            >
              {uploading ? t("_uploading") : t("confirm")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (documentType === "poa") {
    return (
      <div className={classes.documentUploadContainer}>
        <h2>{t("poaTitle")}</h2>
        <ul>
          <li>{t("poaLi1")}</li>
          <li>{t("poaLi2")}</li>
          <li>{t("poaLi3")}</li>
        </ul>
        <h2>{t("example")}</h2>
        <div className="exampleContainer">
          <img
            style={{
              paddingBottom: 4,
              width: "50%",
              margin: "0 auto",
              height: "100%",
            }}
            src={images.illustrations.poaSample}
          />
        </div>
        <div className="uploadContainer">
          <div className="inputs inputsPoa">
            <div
              className={`singleInput ${
                missingFields.missing
                  ? missingFields.fields.includes("billType")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
            >
              <h2>{t("_documentDetails")}</h2>
              <Selector2
                options={billTypes}
                selectorHandle={setBillType}
                selectedOption={billType}
                placeholder={t("documentType")}
              />
            </div>
            <div
              className={`singleInput ${
                missingFields.missing
                  ? missingFields.fields.includes("billDate")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
            >
              {/* <input
                type="date"
                onBlur={(e) => setBillDate(e.target.value)}
                className="textInput"
              /> */}
              <KeyboardDatePicker
                value={billDate}
                onChange={(e) => handleDateChange(e)}
                views={["year", "month", "date"]}
                format="YYYY/MM/DD"
                classes={{ root: classes.picker }}
                placeholder="YYYY/MM/DD"
              />
            </div>
          </div>
          <div className="idFieldInput">
            <h2>{t("poaInfo")}</h2>
            <div
              className={`dragArea ${
                missingFields.missing
                  ? missingFields.fields.includes("bill")
                    ? classes.missingField
                    : ""
                  : ""
              }`}
              onDrop={(e) => handleDrop(e, "poa")}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => browseFiles("poa")}
            >
              {poaFile.file !== null ? <Check /> : <CloudUpload />}
              {poaFile.file !== null ? (
                <p>{poaFile.file.name}</p>
              ) : (
                <p>{t("dragAndDrop")}</p>
              )}
              {poaFile.file !== null ? (
                <p
                  className="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPoa({ photoUrl: "", file: null });
                  }}
                >
                  {t("delete")} <Close />
                </p>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="btnContainer">
            <button
              className="confirmButton"
              disabled={uploading}
              onClick={(e) => confirmAndUpload(e)}
            >
              {uploading ? t("_uploading") : t("confirm")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.hiddenContainer}>
      <Error />
      <p>{t("_selectDocType")}</p>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    account: state.account,
    wasFileUploaded: state.wasFileUploaded,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      pushNotification,
      uploadFile,
      fileUploaded,
    },
    dispatch
  );
}

const styles = (theme) => ({
  picker: {
    width: "100%",
    "& input": {
      ...theme.defaults.input,

      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiInput-underline:after, & .MuiInput-underline:before": {
      borderBottomColor: "transparent",
    },
    "& .MuiOutlinedInput-root": {
      "&:hover": {
        borderColor: "transparent",
      },
    },
  },
  documentUploadContainer: {
    padding: 24,
    "& h2": {
      fontSize: 20,
      fontWeight: 600,
    },
    "& ul": {
      marginLeft: 18,
      marginTop: 8,
      marginBottom: 42,
      "& li": {
        listStyleType: "disc",
        fontSize: 16,
        // fontFamily: "Titillium Web",
        color: theme.colors.text.secondary,
        marginBottom: 4,
        lineHeight: "21px",
      },
    },

    "& .exampleContainer": {
      width: "80%",
      margin: "16px auto 42px",
      display: "flex",
      "& .img": {
        objectFit: "contain",
      },
      "& .example": {
        width: "48.5%",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        "& label": {
          marginBottom: 8,
        },
        "& img": {
          width: "95%",
        },
      },
      [theme.breakpoints.down(520)]: {
        flexDirection: "column",
        alignItems: "center",
        "& .example": {
          marginBottom: 8,
          width: "70%",
        },
      },
    },
    "& .uploadContainer": {
      "& .inputs": {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        "& h2": {
          fontSize: 20,
          fontWeight: 600,
          marginBottom: 16,
        },
        "& .singleInput": {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginBottom: 8,
          "& .textInput": theme.defaults.input,

          "& label": {
            fontSize: 14,
            color: theme.colors.text.secondary,
            marginBottom: 4,
          },
          "& input": {
            marginRight: 0,
            maxWidth: "none !important",
            width: "100%",
          },
        },
        [theme.breakpoints.down(500)]: {
          flexDirection: "column",
          "& .singleInput": {
            width: "100%",
            marginTop: 8,

            "& input": {
              maxWidth: "none !important",
            },
          },
        },
      },
      "& .inputsPoa": {
        justifyContent: "flex-start",
        "& .singleInput": {
          marginRight: 16,
        },
      },
      "& .idFieldInput": {
        marginTop: 42,
        "& .dragArea": {
          height: 170,
          // border: `2px dashed ${theme.colors.misc.dividers}`,
          background: theme.colors.background.lightFore,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 8,
          cursor: "pointer",
          transition: ".3s",
          position: "relative",
          "& p": {
            textAlign: "center",
          },
          "& .delete": {
            position: "absolute",
            right: 16,
            top: 16,
            color: theme.colors.misc.red,
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            fontSize: 12,
            "& svg": {
              fontSize: 16,
            },
          },
          "&:hover": {
            background: theme.colors.background.main,
          },
          "& svg": {
            fontSize: 32,
            color: theme.colors.text.secondary,
          },
        },
      },
    },
    "& .btnContainer": {
      display: "flex",
      justifyContent: "flex-end",
      "& button": theme.defaults.roundBtn,
      "& .confirmButton": {
        background: theme.colors.buttons.green,
        marginTop: 32,
      },
    },
  },
  missingField: {
    borderColor: `${theme.colors.misc.red} !important`,
    "& input, & .selected": {
      borderRadius: 4,
      border: `1px solid ${theme.colors.misc.red} !important`,
    },
  },
  hiddenContainer: {
    width: "100%",
    height: "100%",
    background: theme.colors.background.fore,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "& svg": {
      color: theme.colors.buttons.sell,
      fontSize: 24,
      marginBottom: 12,
    },
    "& p": {
      fontWeight: 600,
      color: theme.colors.text.primary,
    },
  },
});

export default withStyles(styles)(
  withTranslation()(
    connect(mapStateToProps, mapDispatchToProps)(DocumentUpload)
  )
);
